import { readFile, unlink } from "node:fs/promises";
import { resolve } from "node:path";
import { ParseError } from "../core/errors.js";
import { atomicWriteJson, isEnoent } from "./_fs.js";
import { ResumeStateSchema, type ResumeState } from "./resume-state.js";

export interface ResumeStore {
  load(path: string): Promise<ResumeState | null>;
  save(path: string, state: ResumeState): Promise<void>;
  delete(path: string): Promise<void>;
}

export class MemoryResumeStore implements ResumeStore {
  readonly #map = new Map<string, ResumeState>();
  async load(path: string): Promise<ResumeState | null> {
    return this.#map.get(resolve(path)) ?? null;
  }
  async save(path: string, state: ResumeState): Promise<void> {
    this.#map.set(resolve(path), state);
  }
  async delete(path: string): Promise<void> {
    this.#map.delete(resolve(path));
  }
}

export class FilesystemResumeStore implements ResumeStore {
  async load(path: string): Promise<ResumeState | null> {
    let text: string;
    try {
      text = await readFile(path, "utf8");
    } catch (err) {
      if (isEnoent(err)) return null;
      throw err;
    }
    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch (cause) {
      throw new ParseError("Resume state file is corrupt; delete it to start over", { cause, path });
    }
    const parsed = ResumeStateSchema.safeParse(raw);
    if (!parsed.success) {
      throw new ParseError("Resume state file is corrupt; delete it to start over", {
        cause: parsed.error,
        path,
      });
    }
    return parsed.data;
  }

  async save(path: string, state: ResumeState): Promise<void> {
    await atomicWriteJson(path, state);
  }

  async delete(path: string): Promise<void> {
    try {
      await unlink(path);
    } catch (err) {
      if (!isEnoent(err)) throw err;
    }
  }
}
